import React from 'react';
import { History, Clock, CheckCircle, XCircle, User, ArrowRight } from 'lucide-react';
import { useGetDeliveryHistoryQuery } from '../../../store/slices/apiSlice';
import { format } from 'date-fns';

interface DeliveryHistoryProps {
  jobId: string;
}

interface HistoryEntry {
  id: string;
  changed_by_name?: string;
  changed_at: string;
  from_status: 'pending' | 'delivered' | 'incomplete';
  to_status: 'pending' | 'delivered' | 'incomplete';
  notes: string;
}

const statusConfig = {
  pending: {
    label: 'Pending',
    icon: Clock,
    color: 'text-gray-500',
    bgColor: 'bg-gray-100',
    borderColor: 'border-gray-300',
  },
  delivered: {
    label: 'Delivered',
    icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    borderColor: 'border-green-300',
  },
  incomplete: {
    label: 'Incomplete',
    icon: XCircle,
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
    borderColor: 'border-orange-300',
  },
};

export const DeliveryHistory: React.FC<DeliveryHistoryProps> = ({ jobId }) => {
  const { data: history, isLoading, error } = useGetDeliveryHistoryQuery(jobId);

  if (isLoading) {
    return (
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center gap-2 text-gray-500">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
          Loading history...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 rounded-lg p-4">
        <p className="text-red-600 text-sm">Failed to load delivery history</p>
      </div>
    );
  }

  const entries = history || [];

  if (entries.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center gap-2 text-gray-500">
          <History className="w-4 h-4" />
          <span className="text-sm">No history available yet</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-gray-500" />
          <h4 className="font-medium text-gray-900">Delivery History</h4>
          <span className="text-xs text-gray-500">({entries.length} entries)</span>
        </div>
      </div>

      <div className="p-4">
        <div className="space-y-4">
          {entries.map((entry: HistoryEntry, index: number) => {
            const fromConfig = statusConfig[entry.from_status];
            const toConfig = statusConfig[entry.to_status];
            const FromIcon = fromConfig.icon;
            const ToIcon = toConfig.icon;

            return (
              <div
                key={entry.id}
                className={`relative ${index !== entries.length - 1 ? 'pb-4 border-b border-gray-100' : ''}`}
              >
                <div className="flex items-start gap-3">
                  {/* Timeline dot */}
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full ${toConfig.bgColor} flex items-center justify-center`}>
                      <ToIcon className={`w-4 h-4 ${toConfig.color}`} />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    {/* Status change */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${fromConfig.bgColor} ${fromConfig.color}`}>
                        <FromIcon className="w-3 h-3" />
                        {fromConfig.label}
                      </span>
                      <ArrowRight className="w-3 h-3 text-gray-400" />
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${toConfig.bgColor} ${toConfig.color}`}>
                        <ToIcon className="w-3 h-3" />
                        {toConfig.label}
                      </span>
                    </div>

                    {/* Notes */}
                    {entry.notes && (
                      <p className="text-sm text-gray-600 mt-2">{entry.notes}</p>
                    )}

                    {/* Metadata */}
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {entry.changed_by_name || 'Unknown'}
                      </span>
                      <span>•</span>
                      <span>{format(new Date(entry.changed_at), 'MMM d, yyyy h:mm a')}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
